import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import qs from 'query-string';

import {
  getWebGalleryRootId,
  hasWebGalleryContributorAccess,
  hasWebGalleryViewAccess,
  requiresAuthentication,
  requiresEmailConfirmation,
  webGalleryHasPassword,
  webGalleryIsInviteOnly,
} from '../utilities/web-galleries';
import { isAlbumFolderOwner, prependAccountSlug } from '../../../util/account';
import { AccountForWebGalleries } from '../graphql/queries/account';
import { AlbumFolderForWebGalleries } from '../graphql/queries/album-folder';
import { HierarchyAuthTokenVerify } from '../graphql/queries/hierarchy-item';
import { getAccessAuthToken } from '../services/password-store/passwordStore';

const WithWebGalleryFolderPermissionsHOC = WrappedComponent =>
  class WebGalleryFolderPermissions extends Component {
    static propTypes = {
      user: PropTypes.shape({
        email_verified: PropTypes.bool.isRequired,
        email_address: PropTypes.string,
      }),
      loadingUserData: PropTypes.bool,
      folder: PropTypes.shape({
        is_web_gallery: PropTypes.bool,
        is_private_web_gallery: PropTypes.bool,
        has_password: PropTypes.bool,
        invite_only_access: PropTypes.bool,
        contributor_access: PropTypes.shape({
          view_access: PropTypes.string,
        }),
      }),
      loadingFolder: PropTypes.bool,
      authenticated: PropTypes.bool.isRequired,
      staticContext: PropTypes.object,
      location: PropTypes.object,
      hasValidPasswordToken: PropTypes.bool,
      loadingPasswordTokenVerify: PropTypes.bool,
    };

    static defaultProps = {
      folder: {
        account: {},
        children: {
          album_folders: {
            records: [],
          },
          albums: {
            records: [],
          },
        },
      },
    };

    render() {
      const {
        loadingFolder,
        loadingUserData,
        user,
        folder,
        authenticated,
        staticContext,
        location,
        hasValidPasswordToken,
        loadingPasswordTokenVerify,
      } = this.props;

      if (
        (!loadingFolder && !folder) ||
        (folder.id && !folder.is_web_gallery)
      ) {
        return <Redirect to="/not-found" />;
      }

      if (!loadingFolder && requiresAuthentication(folder, authenticated)) {
        if (staticContext) {
          staticContext.referrer = location;
          staticContext.status = 401;
        }

        return <Redirect to="/auth/phone" />;
      }

      // Skip all subsequent permission checks (since ownership trumps them all) and return early.
      if (isAlbumFolderOwner(user, folder)) {
        return <WrappedComponent {...this.props} />;
      }

      // For registered users just via email/password
      // check if they have their emails verified
      if (
        !loadingFolder &&
        !loadingUserData &&
        user &&
        requiresEmailConfirmation(folder, user, authenticated)
      ) {
        return (
          <Redirect
            to={{
              pathname: '/auth/email/request-verification',
              state: {
                emailConfirmUrlRedirect: prependAccountSlug(
                  `/galleries/view/folder/${folder.id}`,
                  folder,
                ),
              },
            }}
          />
        );
      }

      // Invite only access
      //
      // Only contributors with view access can view.
      if (
        !loadingFolder &&
        webGalleryIsInviteOnly(folder) &&
        !hasWebGalleryViewAccess(folder)
      ) {
        return <Redirect to={`/galleries/no-access/${folder.id}`} />;
      }

      // Password authorization check.
      //
      // If the Web Gallery has a password and the user is not password authorized
      // OR not a contributor (contributors get a free pass) redirect to
      // the password screen.
      if (
        !hasWebGalleryContributorAccess(folder) &&
        webGalleryHasPassword(folder) &&
        !loadingPasswordTokenVerify &&
        !hasValidPasswordToken
      ) {
        if (staticContext) {
          staticContext.referrer = location;
          staticContext.status = 401;
        }

        return (
          <Redirect
            to={{
              pathname: `/galleries/password/${folder.id}`,
              search: `?${qs.stringify({
                next: location.pathname,
              })}`,
            }}
          />
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };

const RequireWebGalleryFolderPermissions = WrappedComponent => {
  const WithWebGalleryFolderPermissions = WithWebGalleryFolderPermissionsHOC(
    WrappedComponent,
  );

  const withUserData = graphql(AccountForWebGalleries, {
    props: ({ data: { me, loading } }) => ({
      user: me,
      loadingUserData: loading,
    }),
    skip: ({ authenticated }) => !authenticated,
  });

  const withFolderData = graphql(AlbumFolderForWebGalleries, {
    options: ({ match }) => ({
      variables: {
        id: match.params.folderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      folder: albumFolder,
      loadingFolder: loading,
    }),
    skip: ({ match }) => !match.params.folderId,
  });

  const withHierarchyAuthTokenVerify = graphql(HierarchyAuthTokenVerify, {
    options: ({ folder }) => {
      const rootId = getWebGalleryRootId(folder);
      const token = getAccessAuthToken(rootId);

      return {
        variables: {
          token,
        },
      };
    },
    props: ({ data: { hierarchyAuthTokenVerify, loading } = {} }) => ({
      hasValidPasswordToken: hierarchyAuthTokenVerify?.is_valid,
      loadingPasswordTokenVerify: loading,
    }),
    skip: ({ folder }) => {
      const rootId = getWebGalleryRootId(folder);
      const token = getAccessAuthToken(rootId);

      return !folder || !webGalleryHasPassword(folder) || !token;
    },
  });

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withUserData,
    withFolderData,
    withHierarchyAuthTokenVerify,
  )(WithWebGalleryFolderPermissions);
};

export { WithWebGalleryFolderPermissionsHOC };
export default RequireWebGalleryFolderPermissions;
